// Exchanges — every CEX account under one roof, READ-ONLY.
//
// Operator, 2026-08-28: "funds tracking & visibility rather than logging in &
// out of 20+ accounts +MFA +OTPs hassle everytime." This screen is that roof.
//
// The numbers are pulled by scripts/cex-sync.mjs (CCXT, read-only keys, on the
// Mac) into admin.cex_*, and served by GET /api/cex. Nothing here can move
// funds — there is no action on this screen, by design.
//
// STALE AND UNREACHABLE ARE SHOWN, NOT HIDDEN. A total is only honest next to
// how many accounts behind it failed their last read. `unreachable_accounts`
// gets its own badge and the failed accounts are listed by name — a shrunk
// total that quietly dropped a down venue is the exact failure this screen
// exists to prevent (see bff/src/routes/cex.ts).
//
// This screen reads the BFF through cexGet, a typed GET on lib/api.ts's one client (sign-in, the refusal in words, the bound). Fold it into
// lib/api.ts as `cex` and `cexMovements` and delete it from here — Inventory.tsx carries the same debt.
//
// 14 Sept 2026 (SWEEP-SCOPE §5.28): the subtitle is one clause; the sync
// cadence and the script name are off the screen (cadence in About, "No
// readings yet." for the empty case); the freshness line is "Read 5m ago";
// the empty and failed reads use the app's shapes ("Nothing held.",
// "Couldn't read exchanges — …"); a movement is "In / Out" with the sign.
//
// 16 Sept 2026: a movement the desk's own chain read also saw (the BFF names
// the chain row on it — one rule, bff/src/routes/chain.ts venueLinkSql)
// offers "Record the move →": Move money opens filled in from the venue's
// side, the same door the Chain screen opens from the wallet's side. Still no
// action on a venue here; the booking is the operator's tap on Move money.
//
// 15 Sept 2026: a third tab, FILLS — what the desk bought and sold ON a
// venue, at what price, and what the venue kept (operator: "fetch rates/fees
// data from api & help me book the trades"). One ReportTable, newest first,
// and above it one line per day · venue · pair · side with the totals and
// the cost-weighted rate — the figures a conversion is booked from (per
// Dubai day since 16 Sept 2026: one evening's run is one conversion). Read
// through lib/api.ts (`cexFills`), the first of this screen's reads to live
// there.
//
// 19 Sept 2026 — THE PHONE FORM. Below lg every table on this screen was a
// table of five or eight columns scrolled sideways inside its frame. Now, as on
// Money, Trades and Profit: the lg+ tables stand untouched behind
// "hidden lg:block", and below lg each list draws on the Ledger's card seats
// — the roll-up and the accounts as flat blocks under a LedgerHead (the
// entity, the account), one card per asset (asset · total, then available ·
// in use · accounts); a movement as In/Out pill · account, then stamp ·
// signed amount · Record the move; a fill as Bought/Sold pill · venue ·
// pair · @ price, then stamp · amount for cost · fee. The fills summary
// line breaks after the pair on the phone. Same words, same figures, same
// doors; only the shape below lg changed.
//
// 21 Oct 2026 audit (overnight clean view; operator: "I need clean view for
// operator"): the two alert boxes were drawn at 60% / 40% SOLID (an opacity
// modifier REPLACES a -bg token's own 10% alpha), orange text on orange —
// they are the tokens as they are now. The unreachable box is one row per
// account (name · why · last tried; the fix sentence only for a key that
// needs a human). The advice box shows the title and the action; the detail
// and the rule book sit behind a tap, and a note is its title alone. A failed
// account's figures are muted, never in the live colour, and the header age
// says how many accounts were not read. The desk's name is printed only where
// more than one desk is on screen; an account's own read age only when it is
// stale or failed (the header says the age); "1 account" is not printed under
// a phone asset card. The subtitle is gone — it restated the title and the
// help already says nothing here moves money. A movement is stamped in Dubai
// time, as a fill is.
//
// 21 Oct 2026 design-system sweep: the unreachable headline is "N accounts not read —
// totals may be incomplete" and what to do for a key that needs a human is behind that
// row's own "Details" (a rejected key's row was 17 words); a read that failed — the
// balances, the advice, the movements, the fills — is a kit StateLine with "Try again"
// (red; amber for the advice, which the screen can live without), never a panel of
// nothing; an empty tab is the kit's EmptyState; the fills summary is the figures, the
// fee and the count as three pieces so no block is over the word budget.
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ApiError, readWords } from '../lib/errors';
import { ScreenHeader } from '../components/ScreenHeader';
import { ReportTable, type ReportRow } from '../components/ReportTable';
import { LEDGER, LedgerHead, Delta } from '../components/Ledger';
import { EmptyState, Pending, Pill, StateLine, type StateTone } from '../components/kit';
import { useQuery } from '@tanstack/react-query';
import { RefreshCw, AlertTriangle, CircleSlash } from 'lucide-react';
import { api, request } from '../lib/api';
import { fmtAmount, fmtQuantity, isDustFigure, fmtRate, fmtRelative, fmtStamp, fmtDay, displayAsset, isoToDubaiWall } from '../lib/format';
import { brandCase, sameAsset } from '../lib/naming';

/** How far back the Fills tab reads: a month keeps a run like 14 Sept's on
 *  screen until the next one, and the summary line says the window. */
const FILLS_DAYS = 30;

/** The Movements table's column header — ReportTable's own HEAD string, which
 *  the Fills table beside it uses (components/ReportTable.tsx). */
const MOVE_HEAD = 'px-4 py-[9px] text-label uppercase tracking-[0.06em] text-tg-hint font-medium whitespace-nowrap';

const ENTITY_LABEL: Record<string, string> = { dubaiotc: 'DubaiOTC', indiaotc: 'IndiaOTC', euotc: 'EuOTC' };

// CCXT classifies the failure (scripts/cex-sync.mjs classify()); this turns that
// into what the operator should DO about it. Permanent kinds need a human;
// transient ones clear themselves on the next 10-minute tick, and saying so is
// what stops a flaky venue reading as a missing-funds emergency.
// 21 Oct 2026 audit: the row no longer prints the "transient —" sentences. The
// pill's own word ("timed out", "rate limited") and its warn tone say it, and
// the box heading still says the totals may be incomplete; the fix sentence
// prints only on a row whose error is permanent (a key that needs a human).
const ERROR_KIND: Record<string, { label: string; fix: string }> = {
  permanent:      { label: 'needs attention',  fix: '' },
  auth:           { label: 'key rejected',     fix: 'check the API key/secret (and passphrase) in the keychain' },
  permission:     { label: 'key lacks access', fix: 'the key needs read permission for this wallet' },
  unsupported:    { label: 'not supported',    fix: 'this venue or wallet type cannot be read this way' },
  rate_limit:     { label: 'rate limited',     fix: 'transient — the next sync should succeed' },
  timeout:        { label: 'timed out',        fix: 'transient — the next sync should succeed' },
  venue_down:     { label: 'venue unavailable',fix: 'transient — the exchange is not answering' },
  maintenance:    { label: 'in maintenance',   fix: 'transient — the exchange is in maintenance' },
  clock_skew:     { label: 'clock skew',       fix: "this Mac's clock has drifted from the venue's" },
  network:        { label: 'network error',    fix: 'transient — check this machine is online' },
  exchange_error: { label: 'exchange error',   fix: 'the venue rejected the request' },
  unknown:        { label: 'unknown error',    fix: '' },
};

/**
 * A GET of /api/cex through lib/api.ts's one client: a BFF refusal is one ApiError in words, an expired web session returns to the login screen, and
 * the read is bounded (it was a copy of request() until 26 Oct 2026). It goes the moment /api/cex has a method there.
 */
async function cexGet<T>(path: string): Promise<T> {
  return request<T>(path);
}

// ─── Shapes returned by GET /api/cex (mirror the view columns) ─────────────
interface RollupRow {
  entity: string; asset: string;
  total: string; free: string; used: string;
  accounts: number; stale_accounts: number; unreachable_accounts: number;
  oldest_observation: string | null;
}
interface PositionRow {
  account_id: number; entity: string; venue: string; label: string; status: string;
  wallet: string; asset: string; free: string; used: string; total: string;
  as_of: string; stale: boolean; last_run_ok: boolean | null;
  last_run_error: string | null; last_run_error_kind: string | null;
  last_run_error_permanent: boolean | null; last_run_at: string | null;
}
interface AccountRow {
  account_id: number; entity: string; venue: string; label: string; status: string;
  last_run_ok: boolean | null; last_run_error: string | null;
  last_run_error_kind: string | null; last_run_error_permanent: boolean | null;
  last_run_at: string | null;
}
interface CexResponse { rollup: RollupRow[]; positions: PositionRow[]; accounts: AccountRow[]; entities: string[] }

interface MovementRow {
  /** The books rail for this movement's account, resolved by the BFF (25 Sept 2026). */
  venue_rail?: string | null;
  id: number; entity: string; venue: string; label: string; kind: string; asset: string;
  amount: string; direction: string; fee_amount: string | null; fee_asset: string | null;
  status: string | null; network: string | null; tx_hash: string | null; occurred_at: string;
  /** THE CHAIN ROW THIS MOVEMENT IS (16 Sept 2026) — the desk's own wallet
   *  transfer for the same money, by the one rule bff/src/routes/chain.ts
   *  keeps (venueLinkSql). Null when the chain read saw nothing for it (a
   *  cancelled withdrawal, a pay-away to a third party, an older Worker).
   *  `chain_amount` is what moved on the chain — for a withdrawal the net
   *  the wallet received; `block_time` when. The row offers Move money from
   *  these. */
  chain_transfer_id?: number | null; wallet_slug?: string | null; chain_amount?: string | null;
  block_time?: string | null; chain_tx_hash?: string | null;
}

/** A READ THAT FAILED, as a line of its own (design-system sweep, 21 Oct 2026) — never a panel of nothing: what failed,
 *  the first sentence of what the books said, the rest behind a tap, and the way out. `warn` for a read the screen can
 *  live without (the advice), `bad` for the one it cannot. */
function ReadFailed({ what, error, onRetry, tone = 'bad' }: { what: string; error: unknown; onRetry: () => void; tone?: StateTone }) {
  const words = readWords(error);
  const first = words.split(/(?<=[.!?])\s+/)[0] ?? words;
  const rest = words.slice(first.length).trim();
  return (
    <StateLine
      tone={tone}
      more={rest || undefined}
      action={<button type="button" className="btn-link" onClick={onRetry}>Try again</button>}
    >
      Couldn&rsquo;t read {what}. {first}
    </StateLine>
  );
}

/** THE MOVE MONEY DOOR FROM A MOVEMENT (16 Sept 2026): the same URL the
 *  Chain screen builds from its side, so one tap lands on Move money filled
 *  in — a deposit is wallet → venue, a withdrawal venue → wallet; the amount
 *  is the chain's figure, the venue's fee rides along when it is in the same
 *  coin (the From account pays it), the time is the block's, the note names
 *  the chain hash so the row matches itself after booking. The venue account
 *  in the rail grammar is `exchange.<venue>` (`-india` for the India desk) —
 *  the URL carries it; the screen never says it. Null when the movement meets
 *  no chain row. */
function moveMoneyUrl(m: MovementRow): string | null {
  if (m.chain_transfer_id == null || !m.wallet_slug || !m.chain_amount || !m.block_time) return null;
  // The BFF resolves this now (`venue_rail`, GET /api/cex/movements). Deriving
  // it here from `m.entity` was wrong the moment an account could change desk:
  // a post-move movement on the India account reads entity 'dubaiotc' and got no
  // `-india` suffix, prefilling Dubai's original rail. The fallback keeps an
  // older BFF working; it is the same expression that used to be the only path.
  const venueSlug = m.venue_rail
    ?? `exchange.${m.venue}${m.entity === 'indiaotc' ? '-india' : ''}`;
  const deposit = m.kind === 'deposit';
  const fee = !deposit && m.fee_amount && sameAsset(m.fee_asset, m.asset) && Math.abs(Number(m.fee_amount)) > 0
    ? Math.abs(Number(m.fee_amount)) : 0;
  const q = new URLSearchParams({
    rail: deposit ? m.wallet_slug : venueSlug, railAsset: m.asset,
    to:   deposit ? venueSlug : m.wallet_slug, toAsset: m.asset,
    amount: String(Number(m.chain_amount)),   // the BFF's numeric string, without its trailing zeros
    when: isoToDubaiWall(m.block_time),
    note: `chain ${(m.chain_tx_hash ?? '').slice(0, 14)}`,
  });
  if (fee > 0) q.set('fee', String(fee));
  return `/treasury/rebalance?${q.toString()}`;
}

/** The advisor's rules whose one-sentence action is a PROCEDURE — a head and its steps (A5, "Rotate the read key: create a new one
 *  on the venue, re-add with --replace, …"). Named by the rule's code, never inferred from a colon: M1 reads "Say whose it is:
 *  register it if it is the desk's own wallet, allowlist it with a label if a standing third party, …" and what follows its
 *  colon is the ownership guard (Astra, 22 Oct, round 2b). */
const PROCEDURE_RULES = new Set(['A5']);

/** An advice action as the line and the rest. The action's FIRST sentence is the line, the rest folds behind the row's Details.
 *  A one-sentence procedure (PROCEDURE_RULES) is longer than a line may be: its head before the colon is the line and the steps
 *  fold. Any other single instruction stays whole — it is the guard, and cutting it would hide half of it. */
function splitAction(act: string, rule: string): [string, string] {
  const m = /^(.*?[.!?])\s+(\S[\s\S]*)$/.exec(act);
  if (m) return [m[1] ?? act, m[2] ?? ''];
  const c = /^([^:]{3,60}):\s+(\S[\s\S]*)$/.exec(act);
  if (c && PROCEDURE_RULES.has(rule) && act.split(/\s+/).length > 12) return [c[1] ?? act, c[2] ?? ''];
  return [act, ''];
}

export function Exchanges() {
  const [entity, setEntity] = useState<string | 'all'>('all');
  const [tab, setTab] = useState<'balances' | 'movements' | 'fills'>('balances');
  // DUST FOLDS (operator, 21 Sept 2026, six screenshots: "find the issues"):
  // a row of dust (isDustFigure) is not a holding, and fourteen of them across
  // the account panels were the clutter. Each panel folds them behind one line
  // with the count, as Treasury folds its quiet accounts — nothing dropped, one
  // tap opens it. Keyed per panel.
  const [dustOpen, setDustOpen] = useState<Set<string>>(() => new Set());
  const toggleDust = (k: string) => setDustOpen((prev) => { const n = new Set(prev); if (n.has(k)) n.delete(k); else n.add(k); return n; });
  const isDust = (asset: string, total: string | number) => isDustFigure(total, asset);

  const q = useQuery({
    queryKey: ['cex'],
    queryFn: () => cexGet<CexResponse>('/api/cex'),
    refetchInterval: 60_000,       // the poller writes every 10 min; a 1-min UI refresh keeps "as of" honest
  });
  const mv = useQuery({
    queryKey: ['cex-movements'],
    queryFn: () => cexGet<{ rows: MovementRow[] }>('/api/cex/movements?limit=200'),
    enabled: tab === 'movements',
  });
  // The summary is per venue and carries no desk of its own, so the desk
  // filter is asked of the BFF rather than applied to the rows afterwards.
  const fl = useQuery({
    queryKey: ['cex-fills', entity, FILLS_DAYS],
    queryFn: () => api.cexFills({
      entity: entity === 'all' ? undefined : entity,
      from: new Date(Date.now() - FILLS_DAYS * 24 * 3600 * 1000).toISOString(),
      limit: 500,
    }),
    enabled: tab === 'fills',
  });

  // THE ADVISOR (22 Sept 2026; operator: "a recommendation engine … guard
  // rails to stop operator mistake and best use practices"). One row per
  // thing to do next, from the same rules the terminal and the nightly audit
  // read (admin.cex_advice, db/0049): what is wrong now ("fix") stands open
  // above the tabs; what is merely advised folds behind one line with the
  // count, as the ≈0 rows do. Words only — the terminal carries the
  // commands. Re-read with the balances, every minute.
  // `show` is read by the advisor's useMemo below, which runs during this
  // render — a `const` declared after it is still in its dead zone then
  // ("Cannot access '_' before initialization", 22 Sept 2026, the screen
  // stopped on every load). Declared here, above its first reader.
  const show = (e: string) => entity === 'all' || entity === e;

  const adv = useQuery({
    queryKey: ['cex-advice'],
    queryFn: () => api.cexAdvice(),
    refetchInterval: 60_000,
  });
  const [adviceOpen, setAdviceOpen] = useState(false);
  // An item's detail and its rule book sit behind its own tap (21 Oct 2026
  // audit) — keyed by the advisor's own subject + rule.
  const [detailOpen, setDetailOpen] = useState<Set<string>>(() => new Set());
  const toggleDetail = (k: string) => setDetailOpen((prev) => { const n = new Set(prev); if (n.has(k)) n.delete(k); else n.add(k); return n; });
  const adviceItems = useMemo(
    () => (adv.data?.items ?? []).filter((i) => i.entity === null || show(i.entity)),
    [adv.data, entity],
  );
  const adviceFix = adviceItems.filter((i) => i.severity === 'fix');
  const adviceRest = adviceItems.filter((i) => i.severity !== 'fix');

  const entities = q.data?.entities ?? [];
  const rollup = q.data?.rollup ?? [];
  const positions = q.data?.positions ?? [];
  const accounts = q.data?.accounts ?? [];

  // Accounts that failed (or never ran) their last sync — the row that is work.
  const unreachable = useMemo(
    () => accounts.filter((a) => show(a.entity) && a.last_run_ok !== true),
    [accounts, entity],
  );
  const oldest = useMemo(() => {
    const times = rollup.filter((r) => show(r.entity)).map((r) => r.oldest_observation).filter(Boolean) as string[];
    return times.sort()[0] ?? null;
  }, [rollup, entity]);
  // The desk's name goes before an account's only where more than one desk is
  // on screen — with one desk it was "DubaiOTC ·" on every panel and every
  // alert row (21 Oct 2026 audit). The roll-up panel keeps its entity title.
  const multiDesk = entities.filter(show).length > 1;
  const deskPrefix = (e: string) => (multiDesk ? `${ENTITY_LABEL[e] ?? e} · ` : '');

  // Group positions by account for the drill-down table.
  const byAccount = useMemo(() => {
    const m = new Map<number, { head: PositionRow; lines: PositionRow[] }>();
    for (const p of positions) {
      if (!show(p.entity)) continue;
      if (!m.has(p.account_id)) m.set(p.account_id, { head: p, lines: [] });
      m.get(p.account_id)!.lines.push(p);
    }
    return [...m.values()];
  }, [positions, entity]);

  return (
    <div className="space-y-4 lg:space-y-5">
      <ScreenHeader
        title="Exchanges"
        help={
          <>
            <p>No logins, no OTP — the console only reads what the sync job cached; nothing here can move money.</p>
            <p>Syncs every 10 minutes. Refresh re-reads the cache; an account that failed its last read is listed above the totals.</p>
          </>
        }
        actions={
          <div className="flex items-center gap-3 lg:flex-col lg:items-end lg:gap-1">
            <button
              type="button" onClick={() => q.refetch()} disabled={q.isFetching}
              className="btn-secondary text-body py-1.5 disabled:opacity-50 inline-flex items-center gap-1.5"
            >
              <RefreshCw size={14} className={q.isFetching ? 'animate-spin' : ''} /> Refresh
            </button>
            {/* The age of the reading beside the button (its own line
                under the title before, 20 Sept 2026). A failed read has no
                readings either, so on an error the age of the cached
                reading is still said and only the "no readings" claim —
                the one that would be a guess — is withheld. 21 Oct 2026
                audit: and when accounts failed their last read the line says
                how many ("· 3 not read"), in the warn colour — an age alone
                reads as a calm day. */}
            <span className="text-meta text-tg-hint">
            {oldest
              ? <>Read <span className="font-mono tabular">{fmtRelative(oldest)}</span></>
              : q.isLoading ? <Pending inline />
              : q.isError ? null
              : 'No readings yet.'}
            {unreachable.length > 0 && <span className="text-warn"> · {unreachable.length} not read</span>}
            </span>
          </div>
        }
      />

      {/* entity filter — only the entities this admin can actually see */}
      {entities.length > 1 && (
        <div className="flex flex-wrap gap-2">
          {['all', ...entities].map((e) => (
            <button
              key={e} type="button" onClick={() => setEntity(e)}
              className={`px-3 py-1.5 rounded-lg text-body ${e === entity ? 'bg-tg-secondary' : 'hover:bg-tg-secondary/50'}`}
            >{e === 'all' ? 'All' : (ENTITY_LABEL[e] ?? e)}</button>
          ))}
        </div>
      )}

      {q.isError && <ReadFailed what="exchanges" error={q.error} onRetry={() => q.refetch()} />}

      {/* UNREACHABLE FIRST — a down venue is the one thing that must not hide
          behind a confident-looking total. */}
      {unreachable.length > 0 && (
        // The tokens as they are: `bg-warn-bg` is already a 10% fill, and a
        // `/60` on it REPLACED that alpha (orange text on a 60% orange slab).
        <div className="rounded-lg border border-warn/40 bg-warn-bg p-3">
          {/* The sentence wraps to two lines on a phone: the icon holds
              the first line there, centred on the one line at lg+. */}
          <div className="flex items-start gap-2 text-warn text-body font-medium lg:items-center">
            <AlertTriangle size={15} className="shrink-0 mt-0.5 lg:mt-0" />
            <span>
              {unreachable.length} account{unreachable.length > 1 ? 's' : ''} not read — totals may be incomplete
            </span>
          </div>
          {/* ONE ROW PER ACCOUNT: name · why · last tried. For a key that needs a
              human, what to do is behind the row's own tap (the sweep, 21 Oct 2026:
              a rejected key's row was 17 words) — the pill already says it is rejected. */}
          <ul className="mt-2 space-y-1.5 lg:space-y-1">
            {unreachable.map((a) => {
              const kind = a.last_run_error_kind;
              const fix = a.last_run_error_permanent && kind ? ERROR_KIND[kind]?.fix : '';
              const k = `acct:${a.account_id}`;
              return (
                <li key={a.account_id} className="text-meta text-tg-hint">
                  <div className="flex flex-wrap gap-x-2 gap-y-0.5 items-baseline">
                    <span className="font-medium text-tg-text text-body lg:text-meta">{deskPrefix(a.entity)}{brandCase(a.venue)} · {brandCase(a.label)}</span>
                    {kind && (
                      <Pill tone={a.last_run_error_permanent ? 'bad' : 'warn'}>{ERROR_KIND[kind]?.label ?? kind}</Pill>
                    )}
                    {a.last_run_at ? <span>last tried {fmtRelative(a.last_run_at)}</span> : <span>never synced</span>}
                    {fix && (
                      <button type="button" onClick={() => toggleDetail(k)} className="hover:text-tg-text">
                        {detailOpen.has(k) ? 'Hide' : 'Details'}
                      </button>
                    )}
                  </div>
                  {fix && detailOpen.has(k) && <div className="mt-0.5 break-words">{fix.charAt(0).toUpperCase() + fix.slice(1)}.</div>}
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {/* WHAT TO DO NEXT — the advisor's open items. A "fix" is something a
          venue could see today (a withdrawal to an address the desk cannot
          name, a deposit no wallet of the desk's sent, a pending withdrawal
          a day old); it stands open. The rest folds behind its count. */}
      {adv.isError && <ReadFailed what="the advice" error={adv.error} onRetry={() => adv.refetch()} tone="warn" />}
      {adviceItems.length > 0 && (
        // `bg-neg-bg` as it is — the `/40` modifier replaced its 10% alpha
        // with 40% brick (21 Oct 2026 audit).
        <div className={`rounded-lg border p-3 ${adviceFix.length ? 'border-neg/40 bg-neg-bg' : 'border-tg-secondary/60'}`}>
          <div className="flex items-start gap-2 text-body font-medium lg:items-center">
            <span className="flex-1 min-w-0">
              {adviceFix.length > 0
                ? <span className="text-neg">{adviceFix.length} thing{adviceFix.length > 1 ? 's' : ''} to fix</span>
                // "Nothing to fix" is a claim about NOW: after a failed refresh the list is the last good read, not the books' answer.
                : adv.isError ? <span className="text-tg-hint">Last read</span> : <span>Nothing to fix.</span>}
              {adviceRest.length > 0 && (
                <button type="button" onClick={() => setAdviceOpen((v) => !v)} className="ml-3 text-tg-hint hover:text-tg-text text-meta">
                  {adviceOpen ? 'Fewer' : `More (${adviceRest.length})`}
                </button>
              )}
            </span>
          </div>
          {/* ONE LINE PER ITEM (21 Oct 2026 audit): the title and, for a fix or
              advice, the action. The detail and the rule book sit behind the
              item's own tap. The heading already says "to fix", so a fix row
              wears no pill — a row that is only advised or only noted does.
              A note keeps its action unless that is the "Nothing to do" filler. */}
          <ul className="mt-2 space-y-2">
            {[...adviceFix, ...(adviceOpen ? adviceRest : [])].map((i) => {
              const k = i.subject + i.rule;
              // THE ACTION'S FIRST SENTENCE is the line; the rest of it, the detail and the rule book are behind the row's tap
              // (Astra, 22 Oct: a key-replacement instruction ran three sentences under a row that said "Details"). A one-sentence
              // action — E5, "mirror cooling addresses on the venue's own allowlist" — stays whole and visible.
              const act = i.action && !/^nothing to do\b/i.test(i.action.trim()) ? i.action.trim() : '';
              const [actHead, actRest] = splitAction(act, i.rule);
              const more = !!(actRest || i.detail || i.book);
              const open = more && detailOpen.has(k);
              return (
                <li key={k} className="text-meta">
                  <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                    {i.severity !== 'fix' && (
                      <Pill tone={i.severity === 'advise' ? 'warn' : 'neutral'}>{i.severity === 'advise' ? 'Advised' : 'Noted'}</Pill>
                    )}
                    <span className="font-medium text-tg-text text-body lg:text-meta">{i.title}</span>
                    {more && (
                      <button type="button" onClick={() => toggleDetail(k)} className="text-tg-hint hover:text-tg-text">
                        {open ? 'Hide' : 'Details'}
                      </button>
                    )}
                  </div>
                  {/* A note keeps its action — E5 ("mirror cooling addresses on the venue's own allowlist") is a note
                      and an instruction; only the "Nothing to do" filler is left out (Astra review, 21 Oct 2026). The
                      instruction's head comes first, the folded rest after it — opening Details never reorders the steps. */}
                  {actHead && <div className="mt-0.5 text-tg-text/90">{actHead}</div>}
                  {open && (
                    <div className="mt-0.5 text-tg-hint break-words">
                      {actRest}{actRest && (i.detail || i.book) ? ' · ' : ''}{i.detail}{i.detail && i.book ? ' · ' : ''}{i.book ? `rule book ${i.book}` : ''}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      )}

      <div className="flex gap-2">
        {(['balances', 'movements', 'fills'] as const).map((t) => (
          <button
            key={t} type="button" onClick={() => setTab(t)}
            className={`chip capitalize ${t === tab ? 'chip-on' : ''}`}
          >{t}</button>
        ))}
      </div>

      {tab === 'balances' && (
        <>
          {/* Roll-up: the "one roof" number, per entity per asset. */}
          <section className="space-y-3">
            {entities.filter(show).map((e) => {
              const allRows = rollup.filter((r) => r.entity === e);
              if (allRows.length === 0) return null;
              const dustKey = `r:${e}`;
              const dust = allRows.filter((r) => isDust(r.asset, r.total));
              const rows = dustOpen.has(dustKey) ? allRows : allRows.filter((r) => !isDust(r.asset, r.total));
              const fold = dust.length > 0 ? <DustFold n={dust.length} open={dustOpen.has(dustKey)} onToggle={() => toggleDust(dustKey)} /> : null;
              const anyUsed = allRows.some((r) => Number(r.used) > 0);
              return (
                <div key={e}>
                  {/* ── below lg: the entity's head, then one card per asset
                      — the total beside the asset, the rest of the row's
                      figures on the line under it. */}
                  <div className="lg:hidden card px-2 pt-3 pb-1">
                    <LedgerHead title={ENTITY_LABEL[e] ?? e} />
                    {rows.map((r) => {
                      const split = Number(r.used) > 0;
                      const marked = Number(r.stale_accounts) > 0 || Number(r.unreachable_accounts) > 0;
                      // "1 account" under every card said nothing (21 Oct
                      // 2026 audit): the count is printed from two, or beside
                      // a stale / down mark it qualifies.
                      const counted = Number(r.accounts) !== 1 || marked;
                      return (
                      <div key={r.asset} className={`${LEDGER.card} px-2 last:border-b-0`}>
                        <div className={LEDGER.cardLine1}>
                          <span className="flex-1 min-w-0 truncate font-medium">{displayAsset(r.asset)}</span>
                          <span className="amt shrink-0 whitespace-nowrap">{fmtAmount(r.total, r.asset)}</span>
                        </div>
                        {/* The split only when there is one — "all available"
                            on every line of a phone card is nine lines of
                            nothing (19 Sept 2026). */}
                        {(split || counted) && (
                          <div className={`${LEDGER.cardLine2} flex-wrap gap-y-0.5 text-tg-hint`}>
                            {split && (
                              <span className="whitespace-nowrap">
                                <span className="amt">{fmtAmount(r.free, r.asset)}</span> free · <span className="amt">{fmtAmount(r.used, r.asset)}</span> in use
                              </span>
                            )}
                            {counted && (
                              <span className="ml-auto whitespace-nowrap">
                                {r.accounts} account{Number(r.accounts) === 1 ? '' : 's'}
                                {Number(r.stale_accounts) > 0 && <span className="ml-1 text-warn" title="stale reading">·{r.stale_accounts} stale</span>}
                                {Number(r.unreachable_accounts) > 0 && <span className="ml-1 text-neg" title="failed last read">·{r.unreachable_accounts} down</span>}
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                      );
                    })}
                    {fold && <div className="px-2 py-1.5">{fold}</div>}
                  </div>
                  {/* ── lg+: the framed table, as it always was. */}
                  <div className="hidden lg:block rounded-xl border border-tg-secondary/60 overflow-hidden">
                    <div className="px-4 py-2 bg-tg-secondary/40 text-body font-medium">{ENTITY_LABEL[e] ?? e}</div>
                    <div className="overflow-x-auto">
                      {/* Fixed seats, so the figures stand on one x in every
                          entity's table. Available was the Total again on
                          every row but one; In use says the split, and only
                          where there is one. */}
                      <table className="w-full table-fixed text-body">
                        {/* Every seat sized and the slack in a trailing empty
                            seat (the Ledger's rule), so the figures sit beside
                            the asset, not 600px away; the In use seat only
                            when something is in use; no head over nothing. */}
                        {rows.length > 0 && (
                          <thead className="text-tg-hint text-meta">
                            <tr className="text-left">
                              <th className="w-[10rem] px-4 py-2 font-medium">Asset</th>
                              <th className="w-[12rem] px-4 py-2 font-medium text-right">Total</th>
                              {anyUsed && <th className="w-[12rem] px-4 py-2 font-medium text-right">In use</th>}
                              <th className="w-[9rem] px-4 py-2 font-medium text-right">Accounts</th>
                              <th aria-hidden="true" />
                            </tr>
                          </thead>
                        )}
                        <tbody>
                          {rows.map((r) => (
                            <tr key={r.asset} className="border-t border-tg-secondary/40">
                              <td className="w-[10rem] px-4 py-2 font-medium">{displayAsset(r.asset)}</td>
                              <td className="w-[12rem] px-4 py-2 text-right font-mono tabular whitespace-nowrap">{fmtAmount(r.total, r.asset)}</td>
                              {anyUsed && <td className="w-[12rem] px-4 py-2 text-right font-mono tabular text-tg-hint whitespace-nowrap">{Number(r.used) > 0 ? fmtAmount(r.used, r.asset) : ''}</td>}
                              <td className="w-[9rem] px-4 py-2 text-right text-meta whitespace-nowrap">
                                {r.accounts}
                                {Number(r.stale_accounts) > 0 && <span className="ml-1 text-warn" title="stale reading">·{r.stale_accounts} stale</span>}
                                {Number(r.unreachable_accounts) > 0 && <span className="ml-1 text-neg" title="failed last read">·{r.unreachable_accounts} down</span>}
                              </td>
                              <td aria-hidden="true" />
                            </tr>
                          ))}
                          {fold && (
                            <tr className="border-t border-tg-secondary/40">
                              <td colSpan={anyUsed ? 5 : 4} className="px-4 py-1.5">{fold}</td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              );
            })}
          </section>

          {/* Per-account drill-down. */}
          <section className="space-y-3">
            {/* not a bare heading over a failed read (the sweep, 22 Oct 2026) */}
            {!(q.isError && byAccount.length === 0) && <h2 className="text-body font-medium text-tg-hint">Accounts</h2>}
            {/* "Nothing held" is a claim about the accounts, and a read that
                failed supports no such claim — the same all-clear-on-error
                mistake as the header line above. */}
            {byAccount.length === 0 && q.isSuccess && <EmptyState>Nothing held.</EmptyState>}
            {q.isPending && byAccount.length === 0 && <Pending />}
            {byAccount.map(({ head, lines: allLines }) => {
              const dustKey = `a:${head.account_id}`;
              const dust = allLines.filter((p) => isDust(p.asset, p.total));
              const lines = dustOpen.has(dustKey) ? allLines : allLines.filter((p) => !isDust(p.asset, p.total));
              const fold = dust.length > 0 ? <DustFold n={dust.length} open={dustOpen.has(dustKey)} onToggle={() => toggleDust(dustKey)} /> : null;
              // A FAILED READ'S FIGURES ARE MUTED (21 Oct 2026 audit): the
              // last balance the venue gave is the last one known, not a live
              // one, and white figures under "read failed" read as live. Its
              // own age is said only when it is failed or stale — the header
              // already says the age of the oldest reading.
              const failed = head.last_run_ok === false;
              const aged = failed || head.stale;
              const figTone = failed ? ' text-tg-hint' : '';
              return (
              <div key={head.account_id}>
                {/* ── below lg: the account's head, then one card per wallet
                    · asset — the total beside the asset, free · in use under. */}
                <div className="lg:hidden card px-2 pt-3 pb-1">
                  <LedgerHead
                    title={`${deskPrefix(head.entity)}${brandCase(head.venue)} · ${brandCase(head.label)}`}
                    note={aged ? (
                      <>
                        {failed && <span className="text-neg">read failed · </span>}
                        {head.stale && !failed && <span className="text-warn">stale · </span>}
                        read {fmtRelative(head.as_of)}
                      </>
                    ) : undefined}
                  />
                  {lines.map((p) => (
                    <div key={`${p.wallet}:${p.asset}`} className={`${LEDGER.card} px-2 last:border-b-0`}>
                      <div className={LEDGER.cardLine1}>
                        <span className="flex-1 min-w-0 truncate">
                          <span className="font-medium">{displayAsset(p.asset)}</span>
                          <span className="text-tg-hint capitalize"> · {p.wallet}</span>
                        </span>
                        <span className={`amt shrink-0 whitespace-nowrap${figTone}`}>{fmtAmount(p.total, p.asset)}</span>
                      </div>
                      {Number(p.used) > 0 && (
                        <div className={`${LEDGER.cardLine2} text-tg-hint`}>
                          <span className="whitespace-nowrap">
                            <span className="amt">{fmtAmount(p.free, p.asset)}</span> free · <span className="amt">{fmtAmount(p.used, p.asset)}</span> in use
                          </span>
                        </div>
                      )}
                    </div>
                  ))}
                  {fold && <div className="px-2 py-1.5">{fold}</div>}
                </div>
                {/* ── lg+: the framed table, as it always was. */}
                <div className="hidden lg:block rounded-xl border border-tg-secondary/60 overflow-hidden">
                  <div className="px-4 py-2 flex items-center justify-between gap-3 bg-tg-secondary/30">
                    <div className="text-body font-medium">
                      {deskPrefix(head.entity)}{brandCase(head.venue)} · {brandCase(head.label)}
                    </div>
                    <div className="text-meta flex items-center gap-2">
                      {failed && <span className="inline-flex items-center gap-1 text-neg"><CircleSlash size={12} /> read failed</span>}
                      {head.stale && !failed && <span className="text-warn">stale</span>}
                      {aged && <span className="text-tg-hint">read {fmtRelative(head.as_of)}</span>}
                    </div>
                  </div>
                  <div className="overflow-x-auto">
                    {/* FIXED SEATS (21 Sept 2026): an auto table sized its
                        figure column by whether one row carried "free · in
                        use", so the figures stood at a different x in every
                        panel down the screen. The seats are the same in
                        every panel, so the figures read down one line. */}
                    <table className="w-full table-fixed text-body">
                      <tbody>
                        {lines.map((p) => (
                          <tr key={`${p.wallet}:${p.asset}`} className="border-t border-tg-secondary/30">
                            <td className="w-24 px-4 py-1.5 text-tg-hint text-meta capitalize">{p.wallet}</td>
                            <td className="w-[8rem] px-4 py-1.5 font-medium">{displayAsset(p.asset)}</td>
                            <td className={`w-[12rem] px-4 py-1.5 text-right font-mono tabular whitespace-nowrap${figTone}`}>{fmtAmount(p.total, p.asset)}</td>
                            <td className="w-[22rem] px-4 py-1.5 text-right font-mono tabular text-tg-hint text-meta whitespace-nowrap">
                              {Number(p.used) > 0 ? `${fmtAmount(p.free, p.asset)} free · ${fmtAmount(p.used, p.asset)} in use` : ''}
                            </td>
                            {/* The slack, in a seat of its own (the Ledger's rule). */}
                            <td aria-hidden="true" />
                          </tr>
                        ))}
                        {fold && (
                          <tr className="border-t border-tg-secondary/30">
                            <td colSpan={5} className="px-4 py-1.5">{fold}</td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
              );
            })}
          </section>
        </>
      )}

      {tab === 'movements' && (
        <>
          {/* ── below lg: one card per movement — the In/Out pill and the
              account (network · status muted after it), then the stamp, the
              signed amount and the Record the move door where there is one. */}
          <section className="lg:hidden card p-0 px-2 pt-1 pb-1">
            {(mv.data?.rows ?? []).map((m) => {
              const isIn = m.direction === 'in';
              const moveUrl = moveMoneyUrl(m);
              const aside = [m.network, m.status].filter(Boolean).join(' · ');
              return (
                <div key={m.id} className={`${LEDGER.card} px-2 last:border-b-0`}>
                  <div className={LEDGER.cardLine1}>
                    <Pill tone={isIn ? 'good' : 'bad'} size="md" className="shrink-0 mt-px">{isIn ? 'In' : 'Out'}</Pill>
                    <span className="flex-1 min-w-0 line-clamp-2 break-words">
                      {brandCase(m.venue)} · {brandCase(m.label)}
                      {aside && <span className="text-tg-hint"> · {aside}</span>}
                    </span>
                  </div>
                  {/* Wraps as the fills card does: on a 375px phone the stamp,
                      a five-figure USDT amount and the door together outrun
                      the line, so the door drops to a line of its own
                      (ml-auto keeps it at the right edge either way). */}
                  <div className={`${LEDGER.cardLine2} flex-wrap gap-y-0.5`}>
                    <span className={`${LEDGER.whenText} shrink-0`}>{fmtStamp(m.occurred_at)}</span>
                    <span className="whitespace-nowrap shrink-0">
                      <Delta value={(isIn ? 1 : -1) * Math.abs(Number(m.amount))} asset={m.asset} code />
                    </span>
                    {moveUrl && <Link className="btn-link text-meta ml-auto whitespace-nowrap" to={moveUrl}>Record the move →</Link>}
                  </div>
                </div>
              );
            })}
            {mv.isPending && <div className="px-2 py-2"><Pending /></div>}
            {mv.isError && <div className="px-2 py-3"><ReadFailed what="movements" error={mv.error} onRetry={() => mv.refetch()} /></div>}
            {mv.data && !mv.isError && mv.data.rows.length === 0 && <EmptyState>Nothing here.</EmptyState>}
          </section>
          {/* ── lg+: the table, as it always was. */}
          <section className="hidden lg:block rounded-xl border border-tg-secondary/60 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-body">
                {/* The header is the Fills table's (ReportTable's HEAD) — small
                    capitals, one face for every table on this screen (21 Oct
                    2026 audit). */}
                <thead className="text-tg-hint">
                  <tr className="text-left">
                    <th className={MOVE_HEAD}>When</th>
                    <th className={MOVE_HEAD}>Account</th>
                    <th className={MOVE_HEAD}>In/out</th>
                    <th className={`${MOVE_HEAD} text-right`}>Amount</th>
                    <th className={MOVE_HEAD}>Network</th>
                    <th className={MOVE_HEAD}>Status</th>
                    <th className={MOVE_HEAD}></th>
                  </tr>
                </thead>
                <tbody>
                  {(mv.data?.rows ?? []).map((m) => {
                    const isIn = m.direction === 'in';
                    const moveUrl = moveMoneyUrl(m);
                    return (
                      <tr key={m.id} className="border-t border-tg-secondary/40">
                        {/* Dubai time, as the phone card and a fill print it —
                            "38m ago" was the one stamp here that moved. */}
                        <td className="px-4 py-1.5 text-meta text-tg-hint whitespace-nowrap">{fmtStamp(m.occurred_at)}</td>
                        <td className="px-4 py-1.5 text-meta">{brandCase(m.venue)} · {brandCase(m.label)}</td>
                        <td className={`px-4 py-1.5 font-medium ${isIn ? 'text-pos' : 'text-neg'}`}>{isIn ? 'In' : 'Out'}</td>
                        <td className="px-4 py-1.5 text-right amt">
                          {isIn ? '+' : '−'}{fmtAmount(m.amount, m.asset)} {displayAsset(m.asset)}
                        </td>
                        <td className="px-4 py-1.5 text-meta text-tg-hint">{m.network ?? '—'}</td>
                        <td className="px-4 py-1.5 text-meta text-tg-hint">{m.status ?? '—'}</td>
                        {/* One tap to book the move when the chain saw the same
                            money (moveMoneyUrl); nothing at all when it did not. */}
                        <td className="px-4 py-1.5 text-meta whitespace-nowrap">
                          {moveUrl && <Link className="btn-link text-meta" to={moveUrl}>Record the move →</Link>}
                        </td>
                      </tr>
                    );
                  })}
                  {mv.isPending && <tr><td colSpan={7} className="px-4 py-3"><Pending inline /></td></tr>}
                  {mv.isError && <tr><td colSpan={7} className="px-4 py-3"><ReadFailed what="movements" error={mv.error} onRetry={() => mv.refetch()} /></td></tr>}
                  {mv.data && !mv.isError && mv.data.rows.length === 0 && <tr><td colSpan={7}><EmptyState>Nothing here.</EmptyState></td></tr>}
                </tbody>
              </table>
            </div>
          </section>
        </>
      )}

      {tab === 'fills' && (
        <section className="space-y-3">
          {/* THE SUMMARY LINE — one per day · venue · pair · side over the
              whole window: what was bought or sold, what it cost, the
              cost-weighted rate, the fee. The same figures the Convert screen
              presets a venue conversion from. */}
          {fl.isError && <ReadFailed what="fills" error={fl.error} onRetry={() => fl.refetch()} />}
          {fl.isPending && <Pending />}
          {fl.data && fl.data.summary.length > 0 && (
            <div className="rounded-xl border border-stroke bg-panel px-4 py-3 space-y-2 lg:space-y-1">
              <p className="text-label uppercase tracking-[0.06em] text-tg-hint">Last {FILLS_DAYS} days</p>
              {fl.data.summary.map((g) => {
                const [base, quote] = g.symbol.split('/');
                // One line at lg+; on the phone the day · venue · pair
                // stand on a line of their own and the figures under it.
                return (
                  <p key={`${g.day}|${g.venue}|${g.symbol}|${g.side}`} className="text-body">
                    <span className="block lg:inline">
                      {fmtDay(g.first_at ?? g.day)}{' · '}
                      <span className="font-medium">{brandCase(g.venue)}</span>
                      {' · '}{base} · {quote}<span className="hidden lg:inline">{' · '}</span>
                    </span>
                    <span className="block lg:inline">
                      <span>
                        {g.side === 'buy' ? 'Bought' : 'Sold'}{' '}
                        <span className="amt">{fmtQuantity(g.amount, base)}</span> {base} for{' '}
                        <span className="amt">{fmtAmount(g.cost, quote)}</span> {quote} at{' '}
                        <span className="amt">{fmtRate(g.rate, quote)}</span>
                      </span>
                      <span> · fee {feeWords(g.fees)}</span>
                      <span className="text-tg-hint"> · {g.fills} fill{g.fills === 1 ? '' : 's'}</span>
                    </span>
                  </p>
                );
              })}
            </div>
          )}
          {fl.data && !fl.isError && fl.data.rows.length === 0 && <EmptyState>Nothing here.</EmptyState>}
          {fl.data && fl.data.rows.length > 0 && (
            <>
              {/* ── below lg: one card per fill — the Bought/Sold pill,
                  the venue and the pair, the price at the line's end; then
                  the stamp, the amount for its cost, the fee. */}
              <div className="lg:hidden card p-0 px-2 pt-1 pb-1">
                {fl.data.rows.map((r) => (
                  <div key={r.id} className={`${LEDGER.card} px-2 last:border-b-0`}>
                    <div className={LEDGER.cardLine1}>
                      <Pill tone="info" size="md" className="shrink-0 mt-px">{r.side === 'buy' ? 'Bought' : 'Sold'}</Pill>
                      <span className="flex-1 min-w-0 truncate">
                        <span className="font-medium">{brandCase(r.venue)}</span> · {r.base_asset} · {r.quote_asset}
                      </span>
                      <span className="amt text-tg-hint shrink-0 whitespace-nowrap">@ {fmtRate(r.price, r.quote_asset)}</span>
                    </div>
                    <div className={`${LEDGER.cardLine2} flex-wrap gap-y-0.5`}>
                      <span className={`${LEDGER.whenText} shrink-0`}>{fmtStamp(r.occurred_at)}</span>
                      <span className="whitespace-nowrap">
                        <span className="amt">{fmtQuantity(r.amount, r.base_asset)}</span> <span className="text-tg-hint">{displayAsset(r.base_asset)}</span>
                        {' for '}
                        <span className="amt">{fmtAmount(r.cost, r.quote_asset)}</span> <span className="text-tg-hint">{displayAsset(r.quote_asset)}</span>
                      </span>
                      {r.fee_amount != null && r.fee_asset && (
                        <span className="text-tg-hint whitespace-nowrap">
                          fee <span className="amt">{fmtQuantity(r.fee_amount, r.fee_asset)}</span> {displayAsset(r.fee_asset)}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              {/* ── lg+: the report table, as it always was. */}
              <div className="hidden lg:block">
                <ReportTable
                  columns={[
                    { key: 'when',   label: 'When',     align: 'left' },
                    { key: 'venue',  label: 'Exchange', align: 'left' },
                    { key: 'side',   label: 'Side',     align: 'left' },
                    { key: 'amount', label: 'Amount' },
                    { key: 'pair',   label: 'Pair',     align: 'left' },
                    { key: 'price',  label: 'Price' },
                    { key: 'cost',   label: 'Cost' },
                    { key: 'fee',    label: 'Fee' },
                  ]}
                  rows={fl.data.rows.map((r): ReportRow => ({
                    key: String(r.id),
                    cells: {
                      when:   <span className="text-meta text-tg-hint">{fmtStamp(r.occurred_at)}</span>,
                      venue:  brandCase(r.venue),
                      side:   r.side === 'buy' ? 'Bought' : 'Sold',
                      amount: <>{fmtQuantity(r.amount, r.base_asset)} <span className="text-tg-hint">{displayAsset(r.base_asset)}</span></>,
                      pair:   `${r.base_asset} · ${r.quote_asset}`,
                      price:  fmtRate(r.price, r.quote_asset),
                      cost:   <>{fmtAmount(r.cost, r.quote_asset)} <span className="text-tg-hint">{displayAsset(r.quote_asset)}</span></>,
                      fee:    r.fee_amount != null && r.fee_asset
                        ? <>{fmtQuantity(r.fee_amount, r.fee_asset)} <span className="text-tg-hint">{displayAsset(r.fee_asset)}</span></>
                        : null,
                    },
                  }))}
                  empty="Nothing here."
                />
              </div>
            </>
          )}
        </section>
      )}
    </div>
  );
}

/** "0.00207 BTC + 249.77 USDC", or an em dash when the venue kept nothing. */
function feeWords(fees: Record<string, number>): string {
  const parts = Object.entries(fees).filter(([, v]) => v !== 0).map(([a, v]) => `${fmtQuantity(v, a)} ${displayAsset(a)}`);
  return parts.length ? parts.join(' + ') : '—';
}

/** The one line a panel's dust rows fold behind: the count, and the door. */
function DustFold({ n, open, onToggle }: { n: number; open: boolean; onToggle: () => void }) {
  return (
    <button type="button" onClick={onToggle} className="text-meta text-tg-hint transition-colors hover:text-tg-text">
      {open ? `Hide ${n} near zero` : `+ ${n} near zero`}
    </button>
  );
}
